import { assert, keyBy, keyByMapSafe } from '@varos/util-typescript';
import { ReportingShellLoaderBaseConfig } from '../../base';
import { ReportingSidebarLoader } from './reportingSidebarInterface';
import { useMemo } from 'react';
import { ContentObject } from '@varos/research-sdk';
import { buildSidebarTree } from './reportingSidebarHelper';

export function createReportingSidebarLoader(
    config: ReportingShellLoaderBaseConfig
): ReportingSidebarLoader {
    const {
        repository: { research: repository },
    } = config;

    return {
        useLoad(context, props) {
            const contentPublishedQuery = repository.content.useFind(
                context,
                {
                    status: 'published',
                },
                {
                    suspense: true,
                }
            );
            const contentDraftQuery = repository.content.useFind(
                context,
                {
                    status: 'draft',
                },
                {
                    suspense: true,
                }
            );
            const favoriteQuery = repository.favorite.useFind(
                context,
                {},
                {
                    suspense: true,
                }
            );
            assert(contentPublishedQuery.status === 'success', 'expected suspense');
            assert(contentDraftQuery.status === 'success', 'expected suspense');
            assert(favoriteQuery.status === 'success', 'expected suspense');

            const contentById = useMemo(
                () =>
                    keyBy(
                        [...contentPublishedQuery.data, ...contentDraftQuery.data],
                        'id'
                    ),
                [contentPublishedQuery.data, contentDraftQuery.data]
            );

            const favorites = useMemo(
                () =>
                    favoriteQuery.data.flatMap((favorite) => {
                        const found = contentById[favorite.content];
                        if (!found) {
                            return [];
                        }
                        return [found];
                    }),
                [favoriteQuery.data, contentById]
            );

            const tree = {
                workspace: useMemo(
                    () => buildSidebarTree(contentPublishedQuery.data),
                    [contentPublishedQuery.data]
                ),
                draft: useMemo(
                    () => buildSidebarTree(contentDraftQuery.data),
                    [contentDraftQuery.data]
                ),
                favorites: useMemo(() => buildSidebarTree(favorites), [favorites]),
            };

            // console.log('Built simple tree:', tree);

            return {
                favorites: tree.favorites,
                workspace: tree.workspace,
                draft: tree.draft,
            };
        },
    };
}
